import { useCallback, useMemo, useRef, useState } from 'react'
import { UseFormRegister, useFormState, useWatch } from 'react-hook-form'

import BlockIcon from '@mui/icons-material/Block'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'

import { apiClient } from '~/api'
import { makePaginatedResponse } from '~/api/helper'
import type { UserLocation } from '~/common/schemas/location'
import { ShippingProvider } from '~/common/schemas/shipping'
import { useDimensionHelpers } from '~/products/hooks/useDimensionHelpers'
import {
    type ProductFormSchema,
    type StockUnitFormSchema,
    useProductFormContext,
} from '~/products/hooks/useProductForm'
import { useProductPage } from '~/products/hooks/useProductPage'

function FulfillmentSummary() {
    const { control } = useProductFormContext()
    const { errors } = useFormState({ control, name: 'stock_units' })

    const fulfillmentErrorMessage = useMemo(() => {
        let message = ''

        errors.stock_units?.forEach?.((su) => {
            if (message) {
                return
            }

            if (su?.fulfillment_locations?.type === 'incompleteFulfillment') {
                message = su.fulfillment_locations.message ?? ''
            }
        })

        return message
    }, [errors.stock_units])

    return (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography>{gettext('Fulfillment options')}</Typography>
            {fulfillmentErrorMessage && (
                <Typography variant="subtitle2" color="error">
                    {gettext('You must select at least one fulfillment location')}
                </Typography>
            )}
        </Box>
    )
}

type FulfillmentOptionsTableProps = {
    storeId: number
    stockUnits: StockUnitFormSchema[]
    dimensions: ProductFormSchema['dimensions']
    locations: UserLocation[]
    register: UseFormRegister<ProductFormSchema>
    onShippingProviderChange: (providerId: number, stockUnitIndex?: number) => void
    onShipSeparatelyChange: (checked: boolean, stockUnitIndex?: number) => void
    onLocationChange: (isSelected: boolean, stockUnitIndex: number, location: UserLocation) => void
}

const columnHelper = createColumnHelper<StockUnitFormSchema>()

function FulfillmentOptionsTable({
    storeId,
    stockUnits,
    dimensions,
    locations,
    register,
    onShippingProviderChange,
    onShipSeparatelyChange,
    onLocationChange,
}: FulfillmentOptionsTableProps) {
    const {
        data: shippingProviders,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['stores', { storeId }, 'shipping-providers'],
        queryFn: async () => {
            const json = await apiClient.get(`stores/${storeId}/shipping-providers/`).json()
            return makePaginatedResponse(ShippingProvider).parse(json)
        },
        select: (data) => data.results,
    })

    const { getDimensionOptionName } = useDimensionHelpers()
    const [selectedShippingProvider, setSelectedShippingProvider] = useState<number | null>(null)

    const columns = useMemo(() => {
        return [
            columnHelper.accessor('sku', {
                header: gettext('SKU'),
                cell: ({ row }) => {
                    return row.original.sku || interpolate(gettext('SKU %s'), [row.index + 1])
                },
            }),
            ...dimensions.map((dim) => {
                return columnHelper.display({
                    id: dim.id.toString(),
                    header: Object.values(dim.translations)[0]?.name ?? '',
                    cell: ({ row }) => {
                        const stockUnit = row.original
                        return (
                            <div>
                                {getDimensionOptionName(
                                    dim.index,
                                    stockUnit.dimension_options.find(
                                        (option) => option.product_dimension.index === dim.index
                                    )?.id ?? ''
                                )}
                            </div>
                        )
                    },
                })
            }),
            columnHelper.accessor(
                (row) => row.stock_item.fulfillment_config?.preferred_shipping_provider_id ?? '',
                {
                    id: 'preferred_shipping_provider_id',
                    header: () => {
                        return (
                            <Stack gap={1} sx={{ width: '100%' }}>
                                {isLoading && (
                                    <Skeleton variant="rectangular" width="100%" height={30} />
                                )}

                                {error && (
                                    <Typography variant="subtitle2" color="error">
                                        {gettext('Error loading shipping providers')}
                                    </Typography>
                                )}

                                {shippingProviders && (
                                    <FormControl size="small" sx={{ width: '100%' }}>
                                        <InputLabel id="shipping-provider-label">
                                            {gettext('Shipping provider')}
                                        </InputLabel>
                                        <Select<number>
                                            labelId="shipping-provider-label"
                                            label={gettext('Shipping provider')}
                                            value={selectedShippingProvider ?? ''}
                                            onChange={(e) => {
                                                const providerId =
                                                    typeof e.target.value === 'string'
                                                        ? parseInt(e.target.value)
                                                        : e.target.value

                                                if (isNaN(providerId)) {
                                                    return
                                                }

                                                setSelectedShippingProvider(providerId)
                                                onShippingProviderChange(providerId)
                                            }}
                                        >
                                            {shippingProviders.map((provider) => (
                                                <MenuItem key={provider.id} value={provider.id}>
                                                    {provider.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Stack>
                        )
                    },
                    cell: ({ getValue, row }) => (
                        <>
                            {isLoading && (
                                <Skeleton variant="rectangular" width="100%" height={30} />
                            )}

                            {error && (
                                <Typography variant="subtitle2" color="error">
                                    {gettext('Error loading shipping providers')}
                                </Typography>
                            )}

                            {shippingProviders && (
                                <Select
                                    labelId="shipping-provider-label"
                                    value={getValue() ?? ''}
                                    size="small"
                                    variant="standard"
                                    sx={{ width: '100%' }}
                                    onChange={(e) => {
                                        const providerId =
                                            typeof e.target.value === 'string'
                                                ? parseInt(e.target.value)
                                                : e.target.value

                                        if (isNaN(providerId)) {
                                            return
                                        }

                                        onShippingProviderChange(providerId, row.index)
                                    }}
                                >
                                    {shippingProviders.map((provider) => (
                                        <MenuItem key={provider.id} value={provider.id}>
                                            {provider.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        </>
                    ),
                    size: 200,
                }
            ),
            columnHelper.accessor(
                (row) => row.stock_item.fulfillment_config?.must_ship_separately ?? false,
                {
                    id: 'must_ship_separately',
                    header: () => (
                        <Stack sx={{ alignItems: 'center', width: '100%' }}>
                            <Box>
                                <Checkbox
                                    size="small"
                                    color="secondary"
                                    onChange={(_, checked) => onShipSeparatelyChange(checked)}
                                />
                            </Box>
                            <Typography variant="subtitle2">
                                {gettext('Ship separately')}
                            </Typography>
                        </Stack>
                    ),
                    cell: ({ row, getValue }) => {
                        return (
                            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                                <Checkbox
                                    color="secondary"
                                    size="small"
                                    checked={getValue()}
                                    onChange={(_, checked) => {
                                        onShipSeparatelyChange(checked, row.index)
                                    }}
                                />
                            </Box>
                        )
                    },
                }
            ),
            ...locations.map((location) => {
                return columnHelper.display({
                    id: location.location_id,
                    header: location.name,
                    cell: ({ row }) => {
                        const fulfillmentLocationsIdSet = new Set(
                            row.original.fulfillment_locations.map(
                                (location) => location.location_id
                            )
                        )
                        const isSelected =
                            fulfillmentLocationsIdSet.has(location.location_id) &&
                            location.available_for_fulfillment
                        const isBundle = row.original.stock_item.is_bundle
                        const hasOnlyOneLocation = locations.length === 1

                        if (isBundle) {
                            return (
                                <Tooltip
                                    title={gettext(
                                        'Cannot select fulfillment locations for bundles'
                                    )}
                                >
                                    <BlockIcon color="disabled" />
                                </Tooltip>
                            )
                        }

                        const { ref } = register(`stock_units.${row.index}.fulfillment_locations`, {
                            validate: {
                                incompleteFulfillment: (value, form) => {
                                    const stockItem = form.stock_units[row.index]?.stock_item
                                    return (
                                        stockItem?.is_bundle ||
                                        value.length > 0 ||
                                        gettext('You must select at least one fulfillment location')
                                    )
                                },
                            },
                        })

                        return (
                            <Tooltip
                                title={
                                    hasOnlyOneLocation
                                        ? gettext(
                                              'You must select at least one fulfillment location'
                                          )
                                        : ''
                                }
                            >
                                <Box component="span" sx={{ display: 'flex', width: '100%' }}>
                                    <ButtonBase
                                        ref={ref}
                                        role="option"
                                        aria-label={location.name}
                                        aria-selected={isSelected}
                                        data-selected={isSelected}
                                        disabled={hasOnlyOneLocation}
                                        sx={{
                                            backgroundColor: (theme) => theme.palette.grey[300],
                                            width: '100%',
                                            height: 30,
                                            transition:
                                                'transform 100ms ease-in-out, background-color 100ms ease-in-out',
                                            cursor: 'pointer',

                                            '&[data-selected="true"]': {
                                                backgroundColor: (theme) =>
                                                    theme.palette.primary.main,
                                            },

                                            '&:hover': {
                                                transform: 'scale(1.05)',
                                            },
                                        }}
                                        onClick={() => {
                                            onLocationChange(!isSelected, row.index, location)
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                        )
                    },
                    size: 200,
                })
            }),
        ]
    }, [
        selectedShippingProvider,
        locations,
        dimensions,
        shippingProviders,
        isLoading,
        error,
        register,
        getDimensionOptionName,
        onShippingProviderChange,
        onShipSeparatelyChange,
        onLocationChange,
    ])

    const table = useReactTable({
        data: stockUnits,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.id.toString(),
    })

    const rows = table.getRowModel().rows
    const tableContainerRef = useRef<HTMLDivElement>(null)

    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        estimateSize: () => 52,
        getItemKey: (index) => rows[index]?.id ?? index,
        getScrollElement: () => tableContainerRef.current,
        //measure dynamic row height, except in firefox because it measures table border height incorrectly
        measureElement:
            typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
                ? (element) => element?.getBoundingClientRect().height
                : undefined,
    })

    return (
        <TableContainer
            ref={tableContainerRef}
            sx={{ maxHeight: 320, overflowY: 'auto', position: 'relative' }}
        >
            <Table size="small" sx={{ width: 'auto', display: 'grid' }}>
                <TableHead
                    sx={{
                        display: 'grid',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: (theme) => theme.palette.background.paper,
                    }}
                >
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow
                            key={headerGroup.id}
                            sx={{ display: 'flex', width: '100%', height: 'auto' }}
                        >
                            {headerGroup.headers.map((header) => (
                                <TableCell
                                    key={header.id}
                                    sx={{
                                        width: header.column.getSize(),
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>

                <TableBody
                    sx={{
                        display: 'grid',
                        height: rowVirtualizer.getTotalSize(),
                        position: 'relative',
                    }}
                >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const row = rows[virtualRow.index]
                        if (!row) {
                            return null
                        }

                        return (
                            <TableRow
                                key={row.id}
                                data-index={virtualRow.index}
                                ref={(node) => rowVirtualizer.measureElement(node)}
                                sx={{ width: '100%', position: 'absolute', display: 'flex' }}
                                style={{
                                    transform: `translateY(${virtualRow.start}px)`,
                                }}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell
                                        key={cell.id}
                                        sx={{
                                            width: cell.column.getSize(),
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default function FulfillmentOptions() {
    const { productInfo } = useProductPage()

    const { control, setValue, getValues, register, trigger } = useProductFormContext()
    const stockUnits = useWatch({ control, name: 'stock_units' })
    const dimensions = useWatch({ control, name: 'dimensions', exact: true })

    const [isExpanded, setIsExpanded] = useState(true)

    const handleShippingProviderChange = useCallback(
        (providerId: number, stockUnitIndex?: number) => {
            if (stockUnitIndex !== undefined) {
                setValue(
                    `stock_units.${stockUnitIndex}.stock_item.fulfillment_config.preferred_shipping_provider_id`,
                    providerId
                )
                return
            }

            setValue(
                'stock_units',
                getValues('stock_units').map((su) => ({
                    ...su,
                    stock_item: {
                        ...su.stock_item,
                        fulfillment_config: {
                            ...su.stock_item.fulfillment_config,
                            preferred_shipping_provider_id: providerId,
                        },
                    },
                }))
            )
        },
        [setValue, getValues]
    )

    const handleShipSeparatelyChange = useCallback(
        (checked: boolean, stockUnitIndex?: number) => {
            if (stockUnitIndex !== undefined) {
                setValue(
                    `stock_units.${stockUnitIndex}.stock_item.fulfillment_config.must_ship_separately`,
                    checked
                )
                return
            }

            const currentStockUnits = getValues('stock_units')
            setValue(
                'stock_units',
                currentStockUnits.map((su) => ({
                    ...su,
                    stock_item: {
                        ...su.stock_item,
                        fulfillment_config: {
                            ...su.stock_item.fulfillment_config,
                            must_ship_separately: checked,
                        },
                    },
                }))
            )
        },
        [setValue, getValues]
    )

    const handleLocationChange = useCallback(
        (isSelected: boolean, stockUnitIndex: number, location: UserLocation) => {
            const fulfillmentLocations = getValues(
                `stock_units.${stockUnitIndex}.fulfillment_locations`
            )
            const updatedFulfillmentLocations = isSelected
                ? [...fulfillmentLocations, location]
                : fulfillmentLocations.filter((l) => l.location_id !== location.location_id)

            setValue(
                `stock_units.${stockUnitIndex}.fulfillment_locations`,
                updatedFulfillmentLocations
            )
            void trigger(`stock_units.${stockUnitIndex}.fulfillment_locations`)
        },
        [setValue, getValues, trigger]
    )

    return (
        <div>
            <Accordion expanded={isExpanded} onChange={(_, expanded) => setIsExpanded(expanded)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <FulfillmentSummary />
                </AccordionSummary>

                <AccordionDetails sx={{ flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                            sx={{
                                backgroundColor: (theme) => theme.palette.primary.main,
                                width: 16,
                                height: 16,
                            }}
                        />
                        <Typography variant="subtitle2" color="text.secondary" fontWeight={400}>
                            {gettext('Item can be fulfilled from this location')}
                        </Typography>
                    </Box>

                    <FulfillmentOptionsTable
                        storeId={productInfo.store.id}
                        locations={productInfo.locations}
                        stockUnits={stockUnits}
                        dimensions={dimensions}
                        register={register}
                        onShippingProviderChange={handleShippingProviderChange}
                        onShipSeparatelyChange={handleShipSeparatelyChange}
                        onLocationChange={handleLocationChange}
                    />
                </AccordionDetails>
            </Accordion>
        </div>
    )
}
